const m = require('minthril');
const { component } = require('../../component');
const { Breadcrumbs } = require('../../components/breadcrumbs');
const { Pagination } = require('../../components/pagination');
const { loadAccountDetails } = require('../../controllers/admin');

const AdminAccountPage = component((vnode, emit) => {
  let loadedAccountId = null;
  let account = null;
  let sites = [];
  let billing = null;
  let freeTier = null;
  let billingMode = 'free';
  let billingTestEnabled = false;
  let savingFreeTier = false;
  let grantingDomainTrial = false;
  let error = null;
  let loading = false;
  let sitePageTokens = [null];
  let sitePageIndex = 0;
  let sitePageHasNext = false;
  let siteListRequest = 0;

  async function load(user, accountId, pageIndex = 0) {
    const requestId = ++siteListRequest;
    loadedAccountId = accountId;
    loading = true;
    error = null;
    emit('redraw');

    try {
      const result = await loadAccountDetails(user, accountId, sitePageTokens[pageIndex]);
      if (requestId !== siteListRequest) return;
      account = result.account;
      sites = result.sites;
      sitePageIndex = pageIndex;
      sitePageHasNext = Boolean(result.nextPageToken);
      sitePageTokens = sitePageTokens.slice(0, pageIndex + 1);
      if (result.nextPageToken) sitePageTokens[pageIndex + 1] = result.nextPageToken;
    } catch (reason) {
      if (requestId === siteListRequest) error = reason.message;
    } finally {
      if (requestId === siteListRequest) {
        loading = false;
        emit('redraw');
      }
    }
  }

  function changeSitePage(user, pageIndex) {
    if (loading || pageIndex < 0 || (pageIndex > 0 && !sitePageTokens[pageIndex])) return;
    load(user, loadedAccountId, pageIndex);
  }

  async function loadBilling(onLoadBilling) {
    if (!onLoadBilling || billing) return;
    try {
      billing = await onLoadBilling();
      billingMode = billing.billingMode || 'free';
      billingTestEnabled = billing.billingTestEnabled === true;
      freeTier = { ...billing.freeTier };
      emit('redraw');
    } catch (reason) {
      error = reason.message;
      emit('redraw');
    }
  }

  async function saveFreeTier(onUpdateFreeTier) {
    savingFreeTier = true;
    error = null;
    try {
      const result = await onUpdateFreeTier(billingMode, billingTestEnabled, freeTier);
      billing = {
        ...billing,
        billingMode: result.mode,
        billingTestEnabled: result.billingTestEnabled,
        freeTier: result.freeTier
      };
      billingMode = result.mode;
      billingTestEnabled = result.billingTestEnabled;
      freeTier = { ...result.freeTier };
    } catch (reason) {
      error = reason.message;
    } finally {
      savingFreeTier = false;
      emit('redraw');
    }
  }

  async function grantDomainTrial(onGrantDomainTrial) {
    grantingDomainTrial = true;
    error = null;
    try {
      const result = await onGrantDomainTrial();
      billing = { ...billing, domainTrialEndsAt: result.domainTrialEndsAt };
    } catch (reason) {
      error = reason.message;
    } finally {
      grantingDomainTrial = false;
      emit('redraw');
    }
  }

  return {
    view: ({ attrs: {
      user,
      accountId,
      onLoadBilling,
      onUpdateFreeTier,
      onGrantDomainTrial,
      onImpersonate
    } }) => {
      if (loadedAccountId !== accountId && !loading) {
        sitePageTokens = [null];
        sitePageIndex = 0;
        sitePageHasNext = false;
        load(user, accountId);
      }
      if (account && !billing) {
        loadBilling(onLoadBilling);
      }

      const accountName = account?.displayName || account?.email || accountId;

      return m('section.panel.admin-account-page', [
        m(Breadcrumbs, { items: [
          { label: 'Admin', href: '/admin' },
          { label: accountName }
        ] }),
        error && m('p.error', error),
        loading ? m('p.muted', 'Loading account…') : account && [
          m('h2', accountName),
          account.email && m('p.muted', account.email),
          m('code', account.uid),
          m('nav.admin-account-page__navigation', [
            m('a.button', { href: `/admin/accounts/${encodeURIComponent(accountId)}/billing` }, 'Billing'),
            m('button.button', {
              type: 'button',
              onclick: () => onImpersonate(accountId)
            }, 'View as this account')
          ]),
          billing && freeTier && m('section.admin-account-page__billing', [
            m('section.admin-account-page__domain-trial', [
              m('h3', 'Custom-domain pilot'),
              billing.domainTrialEndsAt
                ? m('p.muted', `Granted until ${new Date(billing.domainTrialEndsAt).toLocaleString()}. The account can connect one custom hostname during the trial.`)
                : m('p.muted', 'Grant this account a 14-day trial for one custom hostname. It does not enable or charge paid billing.'),
              m('button.button', {
                type: 'button',
                disabled: grantingDomainTrial,
                onclick: () => grantDomainTrial(onGrantDomainTrial)
              }, grantingDomainTrial
                ? 'Granting…'
                : billing.domainTrialEndsAt ? 'Restart 14-day trial' : 'Grant 14-day trial')
            ]),
            m('h3', 'Free-tier allowance'),
            m('p.muted', 'Leave the default values in the billing model unchanged for accounts using the standard allowance.'),
            m('form', {
              onsubmit: event => {
                event.preventDefault();
                saveFreeTier(onUpdateFreeTier);
              }
            }, [
              m('label', [
                m('span', 'Billing mode'),
                m('select', {
                  value: billingMode,
                  onchange: event => { billingMode = event.target.value; }
                }, [
                  m('option', { value: 'free' }, 'Free tier — payment setup required for overage'),
                  m('option', { value: 'paid' }, 'Paid — charge overage'),
                  m('option', { value: 'comped' }, 'Comped — never charge unless testing')
                ])
              ]),
              m('label', [
                m('span', 'Sandbox billing test account'),
                m('input', {
                  type: 'checkbox',
                  checked: billingTestEnabled,
                  onchange: event => { billingTestEnabled = event.target.checked; }
                }),
                m('small', 'Allows this account to test Paddle Sandbox checkout and paid-domain provisioning.')
              ]),
              ...[
                ['billableProxiedRequests', 'Requests'],
                ['billableResponseBodyBytes', 'Data (bytes)'],
                ['workerDurationMs', 'Worker time (milliseconds)']
              ].map(([field, label]) => m('label', [
              m('span', label),
              m('input', {
                type: 'number',
                min: 0,
                step: 1,
                value: freeTier[field],
                oninput: event => { freeTier[field] = Number(event.target.value); }
              })
              ])),
              m('button.button', { type: 'submit', disabled: savingFreeTier },
                savingFreeTier ? 'Saving…' : 'Save free tier')
            ])
          ]),
          m('h3', 'Sites'),
          loading && m('p.muted', 'Loading sites…'),
          sites.length ? m('div.admin-account-page__sites', sites.map(site => m('article.admin-account-page__site', [
            m('h4', site.name),
            m('p.muted', site.domains.join(', ') || 'No connected domains'),
            m('nav.admin-account-page__site-links', [
              m('a', { href: `/admin/sites/${site.id}` }, 'Overview'),
              m('a', { href: `/admin/sites/${site.id}/traffic` }, 'Traffic'),
              m('a', { href: `/admin/sites/${site.id}/routes` }, 'Routes'),
              m('a', { href: `/admin/sites/${site.id}/cost` }, 'Cost')
            ])
          ]))) : m('p.muted', 'No configured sites.'),
          (sitePageIndex > 0 || sitePageHasNext) && m(Pagination, {
            page: sitePageIndex,
            hasPrevious: sitePageIndex > 0,
            hasNext: sitePageHasNext,
            loading,
            itemCount: sites.length,
            label: 'sites',
            onChange: pageIndex => changeSitePage(user, pageIndex)
          })
        ]
      ]);
    }
  };
});

module.exports = { AdminAccountPage };
